/**
 * Escritas do DESENHO do mapa: criar setor, renomear/revincular setor e gravar
 * as formas arrastadas. Substitui o `useLayoutDoMapa` da v2 — não existe mais
 * um `/layout` com um retângulo por código fixo: cada setor é uma linha do
 * cadastro e traz a própria forma no `/estado`.
 *
 * Leitura: nenhuma. O estado (e com ele o desenho) chega pelo polling de
 * `useEstadoMapa`; este hook só escreve e devolve o que mudou.
 *
 * Depois de gravar FORMAS o hook pede uma releitura do estado em vez de tentar
 * mesclar a resposta. O `PUT /formas` é tudo-ou-nada em cima da VERSÃO de cada
 * linha: continuar com as versões que a tela tinha na mão garantiria um 409 no
 * arraste seguinte. Uma leitura a mais no fim de um Organizar é barata; versão
 * velha em memória é 409 garantido.
 *
 * Criar e atualizar setor devolvem a própria linha, então essa entra na hora
 * (`aplicarSetor`): o gestor vê a sala nova aparecer sem esperar o ciclo.
 */
import { useCallback, useState } from "react";

import { atualizarSetor, criarSetor, excluirSetor, salvarFormas } from "../../services/mapaProducao";

const MENSAGEM_PADRAO = "Não foi possível salvar o desenho do mapa.";

/**
 * useDesenhoDoMapa({request, aplicarSetor, recarregar}) →
 *   {erro, limparErro, salvarFormasDosSetores(formas), criar(dados),
 *    atualizar(setorId, dados), excluir(setorId, versao)}
 *
 * Toda função lança o erro do backend (a mensagem do 409 vem em `err.message`);
 * quem chama mostra `erro` na tela, junto do botão que resolve, em vez de um
 * toast que some.
 */
export default function useDesenhoDoMapa({ request, aplicarSetor, recarregar }) {
  const [erro, setErro] = useState(null);

  const limparErro = useCallback(() => setErro(null), []);

  const comErro = useCallback(async (acao) => {
    try {
      const resultado = await acao();
      setErro(null);
      return resultado;
    } catch (e) {
      setErro(e?.message || MENSAGEM_PADRAO);
      throw e;
    }
  }, []);

  const salvarFormasDosSetores = useCallback(
    (formas) => comErro(async () => {
      if (!formas.length) return null;
      const resposta = await salvarFormas(request, formas);
      // ESPERA a releitura antes de devolver: quem chama só descarta o rascunho
      // depois disso. Sem o await, o rascunho sumiria antes de o estado novo
      // chegar e a sala daria um pulo de volta para a posição antiga.
      await recarregar?.();
      return resposta;
    }),
    [comErro, request, recarregar]
  );

  const criar = useCallback(
    (dados) => comErro(async () => {
      const setor = await criarSetor(request, dados);
      if (setor) aplicarSetor?.(setor);
      return setor;
    }),
    [comErro, request, aplicarSetor]
  );

  const atualizar = useCallback(
    (setorId, dados) => comErro(async () => {
      const setor = await atualizarSetor(request, setorId, dados);
      if (setor) aplicarSetor?.(setor);
      return setor;
    }),
    [comErro, request, aplicarSetor]
  );

  /**
   * Apaga a sala de vez (só o backend decide se pode: ela precisa nunca ter
   * recebido trabalho). Depois relê o estado — a linha sumiu do servidor e
   * mesclar uma resposta vazia deixaria a sala fantasma no desenho.
   */
  const excluir = useCallback(
    (setorId, versao) => comErro(async () => {
      await excluirSetor(request, setorId, versao);
      await recarregar?.();
      return null;
    }),
    [comErro, request, recarregar]
  );

  return { erro, limparErro, salvarFormasDosSetores, criar, atualizar, excluir };
}
